import { useCallback, useEffect, useMemo } from 'react'
import { API_BASE } from './apiBase'
import { supabase } from './supabase'
import { pushToast } from './toast.jsx'
import { enqueue, drainQueue, watchOnline } from './offlineQueue'

async function authHeader() {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const SILENT_PATHS = new Set(['/profile'])
const QUEUEABLE_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE'])

function shouldToast(path, status) {
  if (SILENT_PATHS.has(path)) return false
  if (status === 401) return true
  if (status === 402) return true
  if (status === 429) return true
  return status >= 500
}

async function rawFetch(path, opts = {}) {
  const auth = await authHeader()
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...auth,
      ...(opts.headers || {}),
    },
  })
}

// Watch for reconnect and drain queue.
let _drainWired = false
function ensureDrainWired() {
  if (_drainWired) return
  _drainWired = true
  watchOnline(async (item) => rawFetch(item.path, { method: item.method, body: item.body }))
  // Initial drain attempt on load
  if (navigator.onLine) drainQueue((item) => rawFetch(item.path, { method: item.method, body: item.body })).catch(() => {})
}

export function useApi() {
  useEffect(ensureDrainWired, [])

  const request = useCallback(async (path, opts = {}) => {
    let res
    try {
      res = await rawFetch(path, opts)
    } catch (netErr) {
      // Offline → queue mutation, return null to caller
      const method = (opts.method || 'GET').toUpperCase()
      if (QUEUEABLE_METHODS.has(method)) {
        try {
          await enqueue({ path, method, body: opts.body })
          pushToast({ kind: 'info', title: 'Offline', body: 'Wird automatisch nachgeholt' })
          return { _queued: true }
        } catch { /* fall through to throw */ }
      }
      pushToast({ kind: 'error', title: 'Netzwerk', body: `${path} unerreichbar` })
      throw netErr
    }
    if (!res.ok) {
      let detail = ''
      try {
        const ct = res.headers.get('content-type') || ''
        const body = ct.includes('json') ? (await res.json())?.detail : await res.text()
        detail = typeof body === 'string' ? body : JSON.stringify(body)
      } catch { /* noop */ }

      // 5xx on writes → queue + recover
      const method = (opts.method || 'GET').toUpperCase()
      if (res.status >= 500 && QUEUEABLE_METHODS.has(method)) {
        try {
          await enqueue({ path, method, body: opts.body })
          pushToast({ kind: 'warn', title: 'Server-Hiccup', body: 'Wird automatisch nachgeholt' })
          return { _queued: true }
        } catch { /* fall through */ }
      }

      if (shouldToast(path, res.status)) {
        const titles = {
          401: 'Nicht eingeloggt',
          402: 'Limit erreicht',
          429: 'Zu viele Anfragen',
          503: 'Server nicht bereit',
        }
        pushToast({
          kind: res.status >= 500 ? 'error' : 'warn',
          title: titles[res.status] || `Fehler ${res.status}`,
          body:  detail?.slice(0, 200) || path,
        })
      }
      const err = new Error(`${res.status} ${res.statusText}: ${detail}`)
      err.status = res.status
      throw err
    }
    if (res.status === 204) return null
    const ct = res.headers.get('content-type') || ''
    return ct.includes('application/json') ? res.json() : res.text()
  }, [])

  // Stable identity — callers use `api` as useEffect dep; a fresh object
  // each render would re-fire effects (e.g. quests/templates/run) on every
  // re-render and saturate the browser connection pool, starving other
  // in-flight requests. Memoize against the stable `request` callback.
  return useMemo(() => ({
    get:    (p)         => request(p),
    post:   (p, body)   => request(p, { method: 'POST',   body: JSON.stringify(body || {}) }),
    patch:  (p, body)   => request(p, { method: 'PATCH',  body: JSON.stringify(body || {}) }),
    put:    (p, body)   => request(p, { method: 'PUT',    body: JSON.stringify(body || {}) }),
    del:    (p)         => request(p, { method: 'DELETE' }),
    raw:    request,
  }), [request])
}
